import { useState, useEffect, useRef } from 'react';
import { Send, Copy, Download, RotateCcw, User, PanelLeftClose, PanelLeft, RefreshCw, ChevronRight, Trash2, MoreVertical, Code, Paperclip, X, FileText, Lightbulb } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import ChatDisclaimer from '../../components/ChatDisclaimer';
import { streamAIResponseWithAutoContinue, exportBubbleToPDF, exportBubbleToDOCX } from '../../services/aiTextGeneratorService';
import { streamEntrepreneurToolResponse, processEntrepreneurToolDocuments, checkEntrepreneurToolConnection } from '../../services/entrepreneurToolService';
import { getAllConversations, getConversation, deleteConversation, renameConversation, setCurrentConversationId, getCurrentConversationId, clearCurrentConversation, autoSaveConversation } from '../../services/conversationHistoryService';
import ConversationHistory from '../ai-text-generator/components/ConversationHistory';
import MarkdownRenderer from '../ai-text-generator/components/MarkdownRenderer';
import { processImagesForUpload } from '../../utils/imageProcessing';
import { saveDocumentAnalysisToHistory } from '../../services/openaiAssistantsService';
import GlobalNavigationMenu from '../../components/GlobalNavigationMenu';
import useTrialGuard from '../../hooks/useTrialGuard';
import TrialPaywall from '../../components/TrialPaywall';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function EntrepreneurToolPage() {
  const CHAT_ID = 'entrepreneur_tool';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamMessage, setCurrentStreamMessage] = useState('');
  const [apiConnected, setApiConnected] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationIdState] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const cancelControllerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const { trialBlocked, setTrialBlocked, checkoutLoading, checkoutError, setCheckoutError, checkBeforeAI, incrementAfterAI, handleAssinarPro } = useTrialGuard('entrepreneur_tool');

  // PRO plan check
  const [isPro, setIsPro] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPlan = async () => {
      if (!user) return;
      try {
        const { data } = await supabase?.from('profiles')?.select('plan')?.eq('id', user?.id)?.single();
        if (data?.plan === 'pro') setIsPro(true);
      } catch {}
    };
    fetchPlan();
  }, [user]);

  // Follow-up suggestions
  const followUpSuggestions = [
    "Explique de forma mais simples",
    "Dê exemplos práticos",
    "Crie um plano de ação",
    "Analise os riscos"
  ];

  const handleNavClick = (path) => {
    window.location.href = path;
  };

  useEffect(() => {
    checkConnection();
    loadConversations();
    restoreLastConversation();
  }, []);

  useEffect(() => {
    if (messages?.length > 0 && currentConversationId) {
      autoSaveConversation(currentConversationId, messages, CHAT_ID);
      loadConversations();
    }
  }, [messages, currentConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamMessage, showTypingIndicator]);

  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef?.current?.scrollHeight, 200) + 'px';
    }
  }, [input]);

  useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      if (document.activeElement === textareaRef?.current) return;

      if (e?.key?.toLowerCase() === 'r' && !e?.shiftKey && !e?.ctrlKey && !e?.metaKey) {
        e?.preventDefault();
        const lastMessage = messages?.[messages?.length - 1];
        if (lastMessage?.role === 'assistant' && !isStreaming) {
          handleRegenerateResponse(messages?.length - 1);
        }
      }

      if (e?.key?.toLowerCase() === 'c' && !e?.shiftKey && !e?.ctrlKey && !e?.metaKey) {
        e?.preventDefault();
        const lastMessage = messages?.[messages?.length - 1];
        if (lastMessage?.role === 'assistant') {
          handleCopy(lastMessage?.content);
        }
      }

      if (e?.key === 'Delete' && !e?.shiftKey && !e?.ctrlKey && !e?.metaKey) {
        e?.preventDefault();
        if (messages?.length > 0 && !isStreaming) {
          handleDeleteMessage(messages?.length - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut);
  }, [messages, isStreaming]);

  const checkConnection = async () => {
    try {
      const status = await checkEntrepreneurToolConnection();
      setApiConnected(status?.connected);
    } catch (error) {
      setApiConnected(false);
    }
  };

  const loadConversations = () => {
    const allConversations = getAllConversations(CHAT_ID);
    setConversations(allConversations);
  };

  const restoreLastConversation = () => {
    const lastConversationId = getCurrentConversationId(CHAT_ID);
    if (lastConversationId) {
      const conversation = getConversation(lastConversationId, CHAT_ID);
      if (conversation) {
        setMessages(conversation?.messages || []);
        setCurrentConversationIdState(lastConversationId);
      }
    }
  };

  const handleSelectConversation = (conversationId) => {
    if (isStreaming) {
      cancelGeneration();
    }

    const conversation = getConversation(conversationId, CHAT_ID);
    if (conversation) {
      setMessages(conversation?.messages || []);
      setCurrentConversationIdState(conversationId);
      setCurrentConversationId(conversationId, CHAT_ID);
      setInput('');
      setCurrentStreamMessage('');
      setProgress(null);
      setShowTypingIndicator(false);
    }
  };

  const handleDeleteConversation = (conversationId) => {
    const success = deleteConversation(conversationId, CHAT_ID);
    if (success) {
      loadConversations();
      
      if (conversationId === currentConversationId) {
        handleNewChat();
      }
    }
  };

  const handleRenameConversation = (conversationId, newTitle) => {
    const success = renameConversation(conversationId, newTitle, CHAT_ID);
    if (success) {
      loadConversations();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelGeneration = () => {
    if (cancelControllerRef?.current) {
      cancelControllerRef?.current?.abort();
      setIsStreaming(false);
      setCurrentStreamMessage('');
      setProgress(null);
      setShowTypingIndicator(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (files?.length === 0) return;

    const allowedDocTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain'
    ];

    const allowedDocExtensions = ['.pdf', '.docx', '.doc', '.txt'];
    const allowedImageTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp'
    ];
    
    const allowedImageExtensions = ['.png', '.jpg', '.jpeg', '.webp'];
    
    const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
    const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024;
    const MAX_FILES = 2;

    if (uploadedFiles?.length + files?.length > MAX_FILES) {
      alert(`Você pode anexar até ${MAX_FILES} arquivos por mensagem para comparação. Você já tem ${uploadedFiles?.length} arquivo(s) anexado(s).`);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const filesToProcess = files?.slice(0, MAX_FILES - uploadedFiles?.length);
    if (files?.length > filesToProcess?.length) {
      alert(`Apenas os primeiros ${filesToProcess?.length} arquivo(s) serão anexados (limite de ${MAX_FILES} arquivos por mensagem).`);
    }

    const validFiles = [];
    const errors = [];

    for (const file of filesToProcess) {
      const fileExtension = file?.name?.substring(file?.name?.lastIndexOf('.'))?.toLowerCase();
      
      const isImage = (
        allowedImageTypes?.includes(file?.type) || 
        allowedImageExtensions?.includes(fileExtension)
      );
      
      const isValidImageFormat = isImage && (
        file?.type === 'image/png' || 
        file?.type === 'image/jpeg' || 
        file?.type === 'image/jpg' || 
        file?.type === 'image/webp' ||
        fileExtension === '.png' ||
        fileExtension === '.jpg' ||
        fileExtension === '.jpeg' ||
        fileExtension === '.webp'
      );
      
      const isDocument = (
        allowedDocTypes?.includes(file?.type) || 
        allowedDocExtensions?.includes(fileExtension)
      );
      
      const isValidDocumentFormat = isDocument && (
        file?.type === 'application/pdf' ||
        file?.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file?.type === 'application/msword' ||
        file?.type === 'text/plain' ||
        fileExtension === '.pdf' ||
        fileExtension === '.docx' ||
        fileExtension === '.doc' ||
        fileExtension === '.txt'
      );

      if (isImage) {
        if (!isValidImageFormat) {
          errors?.push(`Formato de imagem inválido: "${file?.name}". Apenas PNG, JPG, JPEG e WEBP são aceitos.`);
          continue;
        }

        if (file?.size > MAX_IMAGE_SIZE) {
          const fileSizeInMB = (file?.size / (1024 * 1024))?.toFixed(2);
          errors?.push(`A imagem "${file?.name}" é muito grande (${fileSizeInMB}MB). O tamanho máximo permitido para imagens é 5MB.`);
          continue;
        }
        
        if (file?.size === 0) {
          errors?.push(`A imagem "${file?.name}" parece estar vazia ou corrompida. Tente outro arquivo.`);
          continue;
        }

        validFiles?.push({ file, isImage: true });
      } else if (isDocument) {
        if (!isValidDocumentFormat) {
          errors?.push(`Formato de documento inválido: "${file?.name}". Apenas PDF, DOCX, DOC e TXT são aceitos.`);
          continue;
        }
        
        if (file?.size > MAX_DOCUMENT_SIZE) {
          const fileSizeInMB = (file?.size / (1024 * 1024))?.toFixed(2);
          errors?.push(`O documento "${file?.name}" é muito grande (${fileSizeInMB}MB). O tamanho máximo permitido é 50MB.`);
          continue;
        }
        
        if (file?.size === 0) {
          errors?.push(`O documento "${file?.name}" parece estar vazio ou corrompido. Tente outro arquivo.`);
          continue;
        }
        
        validFiles?.push({ file, isImage: false });
      } else {
        errors?.push(`Formato não suportado: "${file?.name}". Para imagens, use PNG, JPG, JPEG ou WEBP. Para documentos, use PDF, DOCX, DOC ou TXT.`);
      }
    }

    if (errors?.length > 0) {
      const errorMessage = errors?.length === 1 
        ? errors?.[0]
        : `Alguns arquivos não puderam ser adicionados:\n\n${errors?.map((err, idx) => `${idx + 1}. ${err}`)?.join('\n')}`;
      
      alert(errorMessage);
    }

    if (validFiles?.length === 0) {
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsProcessingFile(true);

    try {
      const processedFiles = [];
      
      const imageFiles = validFiles?.filter(f => f?.isImage);
      const documentFiles = validFiles?.filter(f => !f?.isImage);
      
      if (imageFiles?.length > 0) {
        const result = await processImagesForUpload(imageFiles?.map(f => ({
          file: f?.file,
          name: f?.file?.name,
          size: f?.file?.size,
          type: f?.file?.type
        })));
        
        result?.processedImages?.forEach(img => {
          processedFiles?.push({
            name: img?.name,
            size: img?.size,
            type: img?.type,
            content: img?.content,
            isImage: true,
            isPDF: false,
            preview: img?.preview,
            file: img?.file
          });
        });
        
        if (result?.failedImages?.length > 0) {
          const failedNames = result?.failedImages?.map(f => f?.name)?.join(', ');
          console.warn(`Imagens ignoradas devido a falha no processamento: ${failedNames}`);
          
          if (result?.processedImages?.length === 0 && documentFiles?.length === 0) {
            alert(`Não foi possível processar as imagens selecionadas. Você pode continuar com mensagem apenas de texto.`);
          }
        }
      }
      
      for (const { file } of documentFiles) {
        processedFiles?.push({
          name: file?.name,
          size: file?.size,
          type: file?.type,
          content: null,
          isImage: false,
          isPDF: file?.type === 'application/pdf' || file?.name?.endsWith('.pdf'),
          preview: null,
          file: file
        });
      }

      if (processedFiles?.length > 0) {
        setUploadedFiles(prev => [...prev, ...processedFiles]);
      }
    } catch (error) {
      console.error('Erro ao processar arquivos:', error);
      
      const errorMsg = error?.message || 'Ocorreu um erro ao processar os arquivos. Verifique se os arquivos não estão corrompidos e tente novamente.';
      alert(errorMsg);
    } finally {
      setIsProcessingFile(false);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = (fileIndex) => {
    setUploadedFiles(prev => prev?.filter((_, idx) => idx !== fileIndex));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input?.trim();
    if ((!textToSend && uploadedFiles?.length === 0) || isStreaming) return;

    // ── TRIAL GUARD ──
    const allowed = await checkBeforeAI();
    if (!allowed) return;
    // ── END TRIAL GUARD ──
    
    setInput('');
    
    if (!currentConversationId || messages?.length === 0) {
      const newConversationId = Date.now()?.toString();
      setCurrentConversationIdState(newConversationId);
      setCurrentConversationId(newConversationId, CHAT_ID);
    }
    
    const images = uploadedFiles?.filter(f => f?.isImage);
    const documents = uploadedFiles?.filter(f => !f?.isImage);
    
    const shouldUseAssistants = documents?.length > 0;
    
    let messageContent = textToSend;
    
    if (uploadedFiles?.length === 2) {
      const fileLabels = ['Arquivo A', 'Arquivo B'];
      messageContent = `[Contexto: Comparar ${fileLabels?.[0]} vs ${fileLabels?.[1]}]\n\n${textToSend || 'Por favor, compare estes dois arquivos e destaque diferenças, semelhanças e insights relevantes.'}`;
    }
    
    if (documents?.length > 0 && !shouldUseAssistants) {
      messageContent += '\n\n--- Arquivos anexados para contexto ---\n';
      
      documents?.forEach((file, index) => {
        const fileLabel = uploadedFiles?.length === 2 ? (index === 0 ? 'Arquivo A' : 'Arquivo B') : null;
        if (file?.isPDF) {
          messageContent += `\n${fileLabel}. ${file?.name} (${formatFileSize(file?.size)}) - PDF (conteúdo em base64)`;
        } else {
          messageContent += `\n${fileLabel}. ${file?.name} (${formatFileSize(file?.size)}):\n\n${file?.content}\n`;
        }
      });
      
      messageContent += '\n--- Fim dos arquivos ---\n\n';
      messageContent += uploadedFiles?.length === 2 ? 'Por favor, analise e compare os arquivos acima.' : 'Por favor, analise o conteúdo dos arquivos acima e responda com base nesse contexto.';
    }
    
    const newMessages = [...messages, { 
      role: 'user', 
      content: shouldUseAssistants ? (uploadedFiles?.length === 2 ? messageContent : textToSend) : messageContent, 
      timestamp: new Date(),
      hasFiles: uploadedFiles?.length > 0,
      filesCount: uploadedFiles?.length,
      images: images?.map((img, idx) => ({ 
        name: img?.name, 
        preview: img?.preview,
        label: uploadedFiles?.length === 2 ? (idx === 0 ? 'Arquivo A' : 'Arquivo B') : null
      })),
      documents: documents?.map((doc, idx) => ({ 
        name: doc?.name, 
        size: doc?.size,
        label: uploadedFiles?.length === 2 ? (idx === 0 ? 'Arquivo A' : 'Arquivo B') : null
      }))
    }];
    setMessages(newMessages);
    
    const documentFilesForAssistants = documents?.map(doc => doc?.file);
    
    setUploadedFiles([]);
    
    try {
      setIsStreaming(true);
      setShowTypingIndicator(true);
      setCurrentStreamMessage('');
      
      cancelControllerRef.current = new AbortController();
      
      let fullResponse = '';
      let responseModel = '';
      let tokensUsed = 0;
      
      if (shouldUseAssistants) {
        console.log('🤖 Using OpenAI Assistants API (gpt-4o-mini) for document reading...');
        
        await new Promise(resolve => setTimeout(resolve, 500));
        setShowTypingIndicator(false);
        
        const assistantPrompt = uploadedFiles?.length === 2 
          ? `${textToSend || 'Compare estes dois arquivos.'}

[Contexto: Arquivo A vs Arquivo B - Análise comparativa solicitada]`
          : (textToSend || 'Analise este documento e forneça um resumo detalhado.');
        
        const result = await processEntrepreneurToolDocuments(
          documentFilesForAssistants,
          assistantPrompt,
          currentConversationId,
          (chunk) => {
            fullResponse += chunk;
            setCurrentStreamMessage(fullResponse);
          },
          (progressInfo) => {
            setProgress({
              status: progressInfo?.status,
              phase: progressInfo?.phase,
              model: progressInfo?.model || responseModel,
              fileName: progressInfo?.fileName
            });
          },
          cancelControllerRef?.current?.signal,
          60000
        );
        
        fullResponse = result?.content;
        responseModel = 'gpt-4o';
        tokensUsed = result?.tokensUsed || 0;
        
        saveDocumentAnalysisToHistory({
          files: documents?.map((doc, idx) => ({
            name: doc?.name,
            size: doc?.size,
            label: documents?.length === 2 ? (idx === 0 ? 'Arquivo A' : 'Arquivo B') : null
          })),
          summary: fullResponse,
          tokensUsed: tokensUsed,
          model: responseModel,
          consolidatedSummary: result?.consolidatedSummary || true,
          isComparison: documents?.length === 2
        });
        
      } else {
        console.log('💬 Using gpt-4o for chat responses...');
        
        const hasVisionContent = images?.length > 0;
        
        const conversationMessages = newMessages?.map(msg => {
          if (msg?.role === 'user' && msg?.images?.length > 0) {
            const content = [
              { type: 'text', text: msg?.content }
            ];
            
            images?.forEach(img => {
              content?.push({
                type: 'image_url',
                image_url: {
                  url: img?.content,
                  detail: 'auto'
                }
              });
            });
            
            return {
              role: msg?.role,
              content: content
            };
          } else {
            return {
              role: msg?.role,
              content: msg?.content
            };
          }
        });
        
        await new Promise(resolve => setTimeout(resolve, 500));
        setShowTypingIndicator(false);
        
        const result = await streamEntrepreneurToolResponse(
          conversationMessages,
          (chunk) => {
            fullResponse += chunk;
            setCurrentStreamMessage(fullResponse);
          },
          (progressInfo) => {
            setProgress(progressInfo);
          },
          cancelControllerRef?.current?.signal
        );
        
        fullResponse = result?.content;
        responseModel = result?.model;
      }
      
      const updatedMessages = [...newMessages, { 
        role: 'assistant', 
        content: fullResponse, 
        timestamp: new Date(),
        model: responseModel,
        cycles: shouldUseAssistants ? 1 : undefined,
        apiType: shouldUseAssistants ? 'assistants' : 'chat',
        tokensUsed: tokensUsed,
        limitReached: false
      }];
      
      setMessages(updatedMessages);
      setCurrentStreamMessage('');
      setProgress(null);
      // ── INCREMENT USAGE ──
      await incrementAfterAI();
      // ── END INCREMENT ──
      
    } catch (error) {
      setShowTypingIndicator(false);
      if (error?.message === 'Geração cancelada pelo usuário' || error?.message === 'Análise cancelada pelo usuário') {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: currentStreamMessage || 'Resposta cancelada pelo usuário.', 
          timestamp: new Date(),
          isCancelled: true
        }]);
      } else {
        const errorMessage = error?.message || 'Erro ao gerar resposta. Tente novamente.';
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: errorMessage,
          timestamp: new Date(),
          isError: true
        }]);
      }
      setCurrentStreamMessage('');
      setProgress(null);
    } finally {
      setIsStreaming(false);
      cancelControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator?.clipboard?.writeText(text);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const handleNewChat = () => {
    if (isStreaming) {
      cancelGeneration();
    }
    setMessages([]);
    setInput('');
    setCurrentStreamMessage('');
    setProgress(null);
    setShowTypingIndicator(false);
    setCurrentConversationIdState(null);
    clearCurrentConversation(CHAT_ID);
  };

  const handleFollowUpClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleRegenerateResponse = async (messageIndex) => {
    if (isStreaming) return;

    const messagesUpToPrompt = messages?.slice(0, messageIndex);
    
    setMessages(messagesUpToPrompt);

    const lastUserMessage = messagesUpToPrompt?.filter(m => m?.role === 'user')?.pop();
    
    if (!lastUserMessage) return;

    try {
      setIsStreaming(true);
      setShowTypingIndicator(true);
      setCurrentStreamMessage('');
      
      cancelControllerRef.current = new AbortController();
      
      let fullResponse = '';
      
      const hasVisionContent = lastUserMessage?.images && lastUserMessage?.images?.length > 0;
      
      const conversationMessages = messagesUpToPrompt?.map(msg => ({
        role: msg?.role,
        content: msg?.content
      }));
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowTypingIndicator(false);
      
      const result = await streamAIResponseWithAutoContinue(
        conversationMessages,
        {},
        (chunk) => {
          fullResponse += chunk;
          setCurrentStreamMessage(fullResponse);
        },
        (progressInfo) => {
          setProgress(progressInfo);
        },
        cancelControllerRef?.current?.signal,
        hasVisionContent
      );
      
      const updatedMessages = [...messagesUpToPrompt, { 
        role: 'assistant', 
        content: result?.content, 
        timestamp: new Date(),
        model: result?.model,
        cycles: result?.totalCycles,
        limitReached: result?.limitReached
      }];
      
      setMessages(updatedMessages);
      setCurrentStreamMessage('');
      setProgress(null);
      
    } catch (error) {
      setShowTypingIndicator(false);
      if (error?.message === 'Geração cancelada pelo usuário') {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: currentStreamMessage || 'Resposta cancelada pelo usuário.', 
          timestamp: new Date(),
          isCancelled: true
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: error?.message || 'Erro ao gerar resposta. Tente novamente.',
          timestamp: new Date(),
          isError: true
        }]);
      }
      setCurrentStreamMessage('');
      setProgress(null);
    } finally {
      setIsStreaming(false);
      cancelControllerRef.current = null;
    }
  };

  const handleContinueResponse = async (messageIndex) => {
    if (isStreaming) return;

    const messageToExtend = messages?.[messageIndex];
    if (messageToExtend?.role !== 'assistant') return;

    const messagesUpToThis = messages?.slice(0, messageIndex + 1);
    
    try {
      setIsStreaming(true);
      setShowTypingIndicator(true);
      setCurrentStreamMessage('');
      
      cancelControllerRef.current = new AbortController();
      
      let fullResponse = messageToExtend?.content;
      
      const lastUserMessage = messagesUpToThis?.filter(m => m?.role === 'user')?.pop();
      const hasVisionContent = lastUserMessage?.images && lastUserMessage?.images?.length > 0;
      
      const conversationMessages = [
        ...messagesUpToThis?.map(msg => ({
          role: msg?.role,
          content: msg?.content
        })),
        {
          role: 'user',
          content: 'Continue de onde parou, expandindo a resposta anterior.'
        }
      ];
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowTypingIndicator(false);
      
      const result = await streamAIResponseWithAutoContinue(
        conversationMessages,
        {},
        (chunk) => {
          fullResponse += chunk;
          setCurrentStreamMessage(fullResponse);
        },
        (progressInfo) => {
          setProgress(progressInfo);
        },
        cancelControllerRef?.current?.signal,
        hasVisionContent
      );
      
      const updatedMessages = messages?.map((msg, idx) => 
        idx === messageIndex 
          ? {
              ...msg,
              content: result?.content,
              model: result?.model,
              cycles: (msg?.cycles || 0) + 1,
              limitReached: false
            }
          : msg
      );
      
      setMessages(updatedMessages);
      setCurrentStreamMessage('');
      setProgress(null);
      
    } catch (error) {
      setShowTypingIndicator(false);
      setCurrentStreamMessage('');
      setProgress(null);
    } finally {
      setIsStreaming(false);
      cancelControllerRef.current = null;
    }
  };

  const handleDeleteMessage = (messageIndex) => {
    if (isStreaming) return;
    
    const messageToDelete = messages?.[messageIndex];
    if (messageToDelete?.role === 'user' && messages?.[messageIndex + 1]?.role === 'assistant') {
      setMessages(messages?.filter((_, idx) => idx !== messageIndex && idx !== messageIndex + 1));
    } else {
      setMessages(messages?.filter((_, idx) => idx !== messageIndex));
    }
  };

  const handleCopyCode = (content) => {
    const codeBlockRegex = /```[\s\S]*?```/g;
    const codeBlocks = content?.match(codeBlockRegex);
    
    if (codeBlocks && codeBlocks?.length > 0) {
      const code = codeBlocks?.map(block => 
        block?.replace(/```[\w]*\n?/, '')?.replace(/```$/, '')
      )?.join('\n\n');
      handleCopy(code);
    } else {
      handleCopy(content);
    }
  };

  return (
    <div id="entrepreneur-tool">
      <div className="h-screen flex" style={{ backgroundColor: '#F8FAFC' }}>
        {/* Sidebar - Conversation History */}
        <div className={`flex-none border-r border-gray-200 transition-all duration-300 ${
          showSidebar ? 'w-64' : 'w-0'
        } overflow-hidden`} style={{ backgroundColor: '#F8FAFC' }}>
          <div className="h-full flex flex-col">
            <div className="flex-none p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold" style={{ color: '#0F172A' }}>Conversas</h2>
                <button
                  onClick={() => setShowSidebar(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Fechar"
                >
                  <PanelLeftClose size={18} style={{ color: '#0B1220' }} />
                </button>
              </div>
              <button
                onClick={handleNewChat}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 hover:opacity-90 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#F97316' }}
              >
                <Icon name="Plus" size={16} />
                <span className="text-sm font-medium">Nova Conversa</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden">
              <ConversationHistory
                conversations={conversations}
                currentConversationId={currentConversationId}
                onSelectConversation={handleSelectConversation}
                onRenameConversation={handleRenameConversation}
                onDeleteConversation={handleDeleteConversation}
              />
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex-none border-b border-gray-200" style={{ backgroundColor: '#F8FAFC' }}>
            <div className="w-full max-w-[860px] mx-auto flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                {!showSidebar && (
                  <button
                    onClick={() => setShowSidebar(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Mostrar histórico"
                  >
                    <PanelLeft size={20} style={{ color: '#0B1220' }} />
                  </button>
                )}
                <GlobalNavigationMenu />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}>
                    <Lightbulb size={18} className="text-white" />
                  </div>
                  <h1 className="text-lg font-semibold" style={{ color: '#0F172A' }}>Ferramenta do(a) Empreendedor(a)</h1>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {messages?.length > 0 && (
                  <button
                    onClick={handleNewChat}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RotateCcw size={16} style={{ color: '#0B1220' }} />
                    <span className="text-sm font-medium hidden sm:inline" style={{ color: '#0B1220' }}>Nova conversa</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages Container - Scrollable */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto"
            style={{ backgroundColor: '#F8FAFC' }}
          >
            <div className="w-full max-w-[860px] mx-auto px-4 py-6">
              {messages?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full space-y-6 py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}>
                    <Lightbulb size={32} className="text-white" />
                  </div>
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Ferramenta ideal para Empreendedores</h2>
                    <p style={{ color: '#0B1220' }}>Encontre sua Solução Aqui</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {messages?.map((message, index) => (
                    <div
                      key={index}
                      className={`group flex gap-3 ${
                        message?.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      {/* Avatar */}
                      <div className={`flex-none w-8 h-8 rounded-full flex items-center justify-center`}
                        style={{
                          background: message?.role === 'user' ? '#F97316' : 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)'
                        }}
                      >
                        {message?.role === 'user' ? (
                          <User size={18} className="text-white" />
                        ) : (
                          <Lightbulb size={18} className="text-white" />
                        )}
                      </div>

                      {/* Message Bubble */}
                      <div
                        className={`flex-1 max-w-[85%] rounded-2xl px-4 py-3 relative ${
                          message?.isError
                            ? 'border border-red-200'
                            : message?.isCancelled
                            ? 'border border-gray-200' : ''
                        }`}
                        style={{
                          backgroundColor: message?.role === 'user' ? '#F97316' 
                            : message?.isError 
                            ? '#FEE2E2'
                            : message?.isCancelled
                            ? '#F3F4F6' : '#FFFFFF',
                          color: message?.role === 'user' ? '#FFFFFF' 
                            : message?.isError 
                            ? '#991B1B' : '#0B1220'
                        }}
                      >
                        {/* Image thumbnails */}
                        {message?.role === 'user' && message?.images && message?.images?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {message?.images?.map((img, imgIndex) => (
                              <div
                                key={imgIndex}
                                className="relative rounded-lg overflow-hidden border-2 border-white/20"
                                style={{ width: '80px', height: '80px' }}
                              >
                                <img
                                  src={img?.preview}
                                  alt={img?.name || `Imagem ${imgIndex + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Document indicators */}
                        {message?.role === 'user' && message?.documents && message?.documents?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {message?.documents?.map((doc, docIndex) => (
                              <div
                                key={docIndex}
                                className="flex items-center gap-2 px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-sm"
                              >
                                <FileText size={16} className="text-white" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate text-white">{doc?.name}</p>
                                  <p className="text-xs text-white/80">{formatFileSize(doc?.size)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {message?.role === 'user' ? (
                          <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {message?.content}
                          </div>
                        ) : (
                          <MarkdownRenderer 
                            content={message?.content} 
                            className="text-sm"
                          />
                        )}
                        
                        {/* Message Controls - Desktop */}
                        {!isStreaming && (
                          <div className={`hidden md:flex items-center gap-1 absolute top-2 ${
                            message?.role === 'user' ? 'left-2' : 'right-2'
                          } opacity-0 group-hover:opacity-100 transition-opacity bg-white shadow-lg rounded-lg p-1`}>
                            {message?.role === 'assistant' && !message?.isError && (
                              <>
                                <button
                                  onClick={() => handleRegenerateResponse(index)}
                                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                  title="Regenerar (R)"
                                >
                                  <RefreshCw size={14} style={{ color: '#0B1220' }} />
                                </button>
                                <button
                                  onClick={() => handleContinueResponse(index)}
                                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                  title="Continuar"
                                >
                                  <ChevronRight size={14} style={{ color: '#0B1220' }} />
                                </button>
                                <button
                                  onClick={() => handleCopyCode(message?.content)}
                                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                  title="Copiar código"
                                >
                                  <Code size={14} style={{ color: '#0B1220' }} />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleCopy(message?.content)}
                              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                              title="Copiar (C)"
                            >
                              <Copy size={14} style={{ color: '#0B1220' }} />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(index)}
                              className="p-1.5 hover:bg-red-50 rounded transition-colors"
                              title="Excluir (Del)"
                            >
                              <Trash2 size={14} className="text-red-600" />
                            </button>
                          </div>
                        )}

                        {/* Message Controls - Mobile */}
                        {!isStreaming && (
                          <div className="md:hidden absolute top-2 right-2">
                            <button
                              onClick={() => setShowMobileMenu(showMobileMenu === index ? null : index)}
                              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                              <MoreVertical size={16} style={{ color: '#0B1220' }} />
                            </button>
                            
                            {showMobileMenu === index && (
                              <>
                                <div 
                                  className="fixed inset-0 z-40"
                                  onClick={() => setShowMobileMenu(null)}
                                />
                                <div className="absolute top-full right-0 mt-1 bg-white shadow-xl rounded-lg p-2 min-w-[180px] z-50 border border-gray-200">
                                  {message?.role === 'assistant' && !message?.isError && (
                                    <>
                                      <button
                                        onClick={() => {
                                          handleRegenerateResponse(index);
                                          setShowMobileMenu(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left"
                                      >
                                        <RefreshCw size={16} style={{ color: '#0B1220' }} />
                                        <span className="text-sm" style={{ color: '#0B1220' }}>Regenerar</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleContinueResponse(index);
                                          setShowMobileMenu(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left"
                                      >
                                        <ChevronRight size={16} style={{ color: '#0B1220' }} />
                                        <span className="text-sm" style={{ color: '#0B1220' }}>Continuar</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleCopyCode(message?.content);
                                          setShowMobileMenu(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left"
                                      >
                                        <Code size={16} style={{ color: '#0B1220' }} />
                                        <span className="text-sm" style={{ color: '#0B1220' }}>Copiar código</span>
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => {
                                      handleCopy(message?.content);
                                      setShowMobileMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-left"
                                  >
                                    <Copy size={16} style={{ color: '#0B1220' }} />
                                    <span className="text-sm" style={{ color: '#0B1220' }}>Copiar texto</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleDeleteMessage(index);
                                      setShowMobileMenu(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors text-left"
                                  >
                                    <Trash2 size={16} className="text-red-600" />
                                    <span className="text-sm text-red-600">Excluir</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {message?.role === 'assistant' && !message?.isError && (
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                              {message?.apiType === 'assistants' && (
                                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FED7AA', color: '#9A3412' }}>
                                  Assistants API
                                </span>
                              )}
                              {message?.cycles && (
                                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
                                  {message?.cycles} {message?.cycles > 1 ? 'ciclos' : 'ciclo'}
                                </span>
                              )}
                              {message?.tokensUsed > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                                  {message?.tokensUsed} tokens
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleCopy(message?.content)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Copiar"
                              >
                                <Copy size={14} style={{ color: '#0B1220' }} />
                              </button>
                              <button
                                onClick={() => exportBubbleToPDF(message?.content)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Baixar PDF"
                              >
                                <Download size={14} style={{ color: '#0B1220' }} />
                              </button>
                              <button
                                onClick={() => exportBubbleToDOCX(message?.content)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Baixar DOCX"
                              >
                                <FileText size={14} style={{ color: '#0B1220' }} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Typing Indicator */}
                  {showTypingIndicator && (
                    <div className="flex gap-3">
                      <div className="flex-none w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)' }}>
                        <Lightbulb size={18} className="text-white" />
                      </div>
                      <div className="bg-white rounded-2xl px-4 py-3">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#9CA3AF', animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#9CA3AF', animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#9CA3AF', animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Streaming Message */}
                  {isStreaming && currentStreamMessage && !showTypingIndicator && (
                    <div className="flex gap-3">
                      <div className="flex-none w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)' }}>
                        <Lightbulb size={18} className="text-white" />
                      </div>
                      <div className="flex-1 max-w-[85%] rounded-2xl px-4 py-3 bg-white" style={{ color: '#0B1220' }}>
                        <MarkdownRenderer 
                          content={currentStreamMessage} 
                          className="text-sm"
                        />
                        <span className="inline-block w-1 h-4 ml-1 animate-pulse" style={{ backgroundColor: '#F97316' }}></span>
                        {progress && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-2 text-xs" style={{ color: '#6B7280' }}>
                              {progress?.phase === 'uploading' && (
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#F97316' }}></div>
                                  <span>{progress?.status}</span>
                                </div>
                              )}
                              {progress?.phase === 'reading' && (
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#F97316' }}></div>
                                  <span>{progress?.status}</span>
                                </div>
                              )}
                              {progress?.phase === 'generating' && (
                                <div className="flex items-center gap-2">
                                  <div className="flex gap-1">
                                    <div className="w-1 h-3 bg-orange-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                    <div className="w-1 h-3 bg-orange-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                    <div className="w-1 h-3 bg-orange-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                  </div>
                                  <span>A resposta está sendo gerada...</span>
                                </div>
                              )}
                              {progress?.phase === 'retrying' && (
                                <div className="flex items-center gap-2">
                                  <RefreshCw size={12} className="animate-spin" style={{ color: '#F59E0B' }} />
                                  <span className="text-amber-600">{progress?.status}</span>
                                </div>
                              )}
                              {!['uploading', 'reading', 'generating', 'retrying']?.includes(progress?.phase) && (
                                <span>{progress?.status}</span>
                              )}
                            </div>
                            {progress?.fileName && (
                              <p className="text-xs mt-1 truncate" style={{ color: '#9CA3AF' }}>
                                {progress?.fileName}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Follow-up Suggestions */}
                  {messages?.length > 0 && 
                   messages?.[messages?.length - 1]?.role === 'assistant' && 
                   !isStreaming && (
                    <div className="flex flex-wrap gap-2 pl-11">
                      {followUpSuggestions?.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleFollowUpClick(suggestion)}
                          className="px-3 py-1.5 text-xs bg-white border hover:bg-orange-50 rounded-full transition-colors"
                          style={{ 
                            borderColor: '#E5E7EB',
                            color: '#0B1220'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#F97316'}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = '#E5E7EB'}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>

          {/* Input Area */}
          <div className="flex-none border-t border-gray-200" style={{ backgroundColor: '#F8FAFC' }}>
            <div className="w-full max-w-[860px] mx-auto px-4 py-4">
              {uploadedFiles?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {uploadedFiles?.map((file, index) => {
                    const fileLabel = uploadedFiles?.length === 2 ? (index === 0 ? 'Arquivo A' : 'Arquivo B') : null;
                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm ${
                          file?.isImage ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'
                        }`}
                      >
                        {file?.isImage ? (
                          <>
                            <img 
                              src={file?.preview} 
                              alt={file?.name}
                              className="w-10 h-10 object-cover rounded flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              {fileLabel && (
                                <span className="text-xs font-bold text-purple-900 block mb-0.5">{fileLabel}</span>
                              )}
                              <p className="font-medium truncate text-purple-900">{file?.name}</p>
                              <p className="text-xs text-purple-600">{formatFileSize(file?.size)}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <FileText size={16} style={{ color: '#F97316' }} className="flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              {fileLabel && (
                                <span className="text-xs font-bold block mb-0.5" style={{ color: '#9A3412' }}>{fileLabel}</span>
                              )}
                              <p className="font-medium truncate" style={{ color: '#9A3412' }}>{file?.name}</p>
                              <p className="text-xs" style={{ color: '#F97316' }}>{formatFileSize(file?.size)}</p>
                            </div>
                          </>
                        )}
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className={`p-1 rounded transition-colors flex-shrink-0 ${
                            file?.isImage ? 'hover:bg-purple-100' : 'hover:bg-orange-100'
                          }`}
                          title="Remover arquivo"
                        >
                          <X size={14} className={file?.isImage ? 'text-purple-900' : 'text-orange-900'} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-end gap-3 bg-white rounded-2xl p-3 border border-gray-200">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isStreaming || isProcessingFile || uploadedFiles?.length >= 2}
                />
                <button
                  onClick={() => fileInputRef?.current?.click()}
                  disabled={isStreaming || isProcessingFile || uploadedFiles?.length >= 2}
                  className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                    isStreaming || isProcessingFile || uploadedFiles?.length >= 2
                      ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-50 border border-gray-300'
                  }`}
                  title={uploadedFiles?.length >= 2 
                    ? "Limite de 2 arquivos atingido" 
                    : "Anexar até 2 arquivos — PDF/DOCX/TXT (até 50 MB cada) ou imagem PNG/JPG/WEBP (até 5 MB cada)"}
                >
                  {isProcessingFile ? (
                    <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#9CA3AF' }} />
                  ) : (
                    <Paperclip size={18} style={{ color: uploadedFiles?.length >= 2 ? '#9CA3AF' : '#0B1220' }} />
                  )}
                </button>

                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e?.target?.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Como posso ajudar?"
                  className="flex-1 bg-transparent border-none outline-none resize-none text-sm max-h-[200px]"
                  style={{ color: '#0B1220' }}
                  rows={1}
                  disabled={isStreaming}
                />
                {isStreaming ? (
                  <button
                    onClick={cancelGeneration}
                    className="p-2.5 rounded-xl transition-colors flex-shrink-0"
                    style={{ backgroundColor: '#EF4444' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
                  >
                    <div className="w-4 h-4 border-2 border-white"></div>
                  </button>
                ) : (
                  <button
                    onClick={() => handleSend()}
                    disabled={!input?.trim() && uploadedFiles?.length === 0}
                    className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                      input?.trim() || uploadedFiles?.length > 0
                        ? '' : 'cursor-not-allowed'
                    }`}
                    style={{
                      backgroundColor: (input?.trim() || uploadedFiles?.length > 0) ? '#F97316' : '#E5E7EB',
                      color: (input?.trim() || uploadedFiles?.length > 0) ? '#FFFFFF' : '#9CA3AF'
                    }}
                    onMouseEnter={(e) => {
                      if (input?.trim() || uploadedFiles?.length > 0) {
                        e.currentTarget.style.backgroundColor = '#EA580C';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (input?.trim() || uploadedFiles?.length > 0) {
                        e.currentTarget.style.backgroundColor = '#F97316';
                      }
                    }}
                  >
                    <Send size={18} />
                  </button>
                )}
              </div>
            </div>
            <ChatDisclaimer />
          </div>
        </div>
      </div>
      {trialBlocked && !isPro && (
        <TrialPaywall
          onAssinarPro={handleAssinarPro}
          checkoutLoading={checkoutLoading}
          checkoutError={checkoutError}
          onDismissError={() => setCheckoutError(null)}
          onClose={() => setTrialBlocked(false)}
        />
      )}
    </div>
  );
}